/* src/pages/admin/AdminZipcodeDispatchZonesPage.tsx */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminZipcodeDispatchZoneService,
    AdminZipcodeDispatchZone,
    GetZipcodeDispatchZonesParams
} from "../../services/admin.zipcode-dispatch-zone.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faCircleNotch,
    faTriangleExclamation,
    faGlobe,
    faMapPin,
    faToggleOn,
    faEye,
    faUserCheck,
    faUserSlash,
    faEdit,
    faTrash,
    faPlus,
    faMapMarkedAlt
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminZipcodeDispatchZonesPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import CreateZipcodeDispatchZoneModal from "../../components/admin/CreateZipcodeDispatchZoneModal";
import UpdateZipcodeDispatchZoneModal from "../../components/admin/UpdateZipcodeDispatchZoneModal";

const AdminZipcodeDispatchZonesPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    // Check permissions
    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [zones, setZones] = useState<AdminZipcodeDispatchZone[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedZone, setSelectedZone] = useState<AdminZipcodeDispatchZone | null>(null);

    const canCreate = permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "CREATE");
    const canUpdate = permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "UPDATE");
    const canDelete = permissionCheckerService.checkPermission(adminData, "ZIPCODE DISPATCH ZONE", "DELETE");

    const fetchZones = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetZipcodeDispatchZonesParams = {
                page: page,
                pageSize: size,
                status: statusFilter === "" ? undefined : Number(statusFilter),
                SearchString: searchText || undefined,
            };

            const response = await adminZipcodeDispatchZoneService.getZipcodeDispatchZones(params);

            if (response.statusCode === 200) {
                setZones(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching zipcode dispatch zones:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchZones(1, perPage);
    }, [statusFilter]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchZones(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchZones(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchZones(1, perPage);
    };

    const handleToggleStatus = async (zone: AdminZipcodeDispatchZone) => {
        const newStatus = zone.status === 1 ? 0 : 1;
        const actionText = newStatus === 1 ? "activar" : "desactivar";

        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: `Esta acción procederá a ${actionText} la zona de despacho para el código postal ${zone.zipcode}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: newStatus === 1 ? "#10b981" : "#f59e0b",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: `Sí, ${actionText}`,
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminZipcodeDispatchZoneService.changeZipcodeDispatchZoneStatus({
                    id: zone.id,
                    status: newStatus,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: newStatus === 1 ? "Activada" : "Desactivada",
                        text: `La zona de despacho ha sido ${newStatus === 1 ? "activada" : "desactivada"} con éxito.`,
                    });
                    fetchZones(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error updating zipcode dispatch zone status:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDelete = async (zone: AdminZipcodeDispatchZone) => {
        const result = await Swal.fire({
            title: "¿Eliminar zona de despacho?",
            text: `Esta acción eliminará de forma lógica la zona de despacho con código postal ${zone.zipcode}.`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminZipcodeDispatchZoneService.deleteZipcodeDispatchZone({
                    id: zone.id,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Eliminada",
                        text: "La zona de despacho ha sido eliminada con éxito.",
                    });
                    fetchZones(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error deleting zipcode dispatch zone:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const columns: TableColumn<AdminZipcodeDispatchZone>[] = [
        {
            name: "País",
            selector: (row) => row.iso2Country || row.isO2Country || "",
            sortable: true,
            width: "120px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2 py-2">
                    <FontAwesomeIcon icon={faGlobe} className="text-muted" />
                    <span style={{ fontWeight: 600 }}>{row.iso2Country || row.isO2Country}</span>
                </div>
            )
        },
        {
            name: "Código Postal",
            selector: (row) => row.zipcode,
            sortable: true,
            minWidth: "140px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faMapPin} className="text-muted" />
                    <span>{row.zipcode}</span>
                </div>
            )
        },
        {
            name: "Ciudad",
            selector: (row) => row.city || "-",
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Estado",
            selector: (row) => row.state || "-",
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Estado",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`${styles.badge} ${row.status === 1 ? styles.statusActive : styles.statusInactive}`}>
                    {row.status === 1 ? "Activo" : "Inactivo"}
                </span>
            ),
            width: "120px",
        },
        {
            name: "Acciones",
            width: "180px",
            cell: (row) => (
                <div className="d-flex gap-2">
                    <button
                        className={styles.detailButton}
                        onClick={() => window.open(`/admin/zipcode-dispatch-zone/${row.id}`, "_blank")}
                        title="Ver detalles"
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                    {canUpdate && (
                        <button
                            className={styles.detailButton}
                            onClick={() => {
                                setSelectedZone(row);
                                setShowEditModal(true);
                            }}
                            title="Editar zona"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className={row.status === 1 ? styles.deactivateButton : styles.activateButton}
                            onClick={() => handleToggleStatus(row)}
                            title={row.status === 1 ? "Desactivar" : "Activar"}
                        >
                            <FontAwesomeIcon icon={row.status === 1 ? faUserSlash : faUserCheck} />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            className={styles.deleteButton}
                            onClick={() => handleDelete(row)}
                            title="Eliminar"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ),
            right: true,
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "60px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminZipcodeContainer}>
                <DashboardCard
                    icon={faMapMarkedAlt}
                    title="Zonas de Despacho por Código Postal"
                    description="Gestiona las zonas de entrega admitidas y activas basadas en códigos postales y países."
                    actions={
                        canCreate && (
                            <button
                                className={btnStyles.primary}
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-2" /> Registrar Zona
                            </button>
                        )
                    }
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Estado</label>
                                <select
                                    className={styles.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="1">Activos</option>
                                    <option value="0">Inactivos</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por código postal, ciudad, estado, país..."
                                    className={styles.searchInput}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchZones(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : zones.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faMapMarkedAlt} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron zonas de despacho con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={zones}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando zonas de despacho...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>

            <CreateZipcodeDispatchZoneModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={() => fetchZones(currentPage, perPage)}
            />

            <UpdateZipcodeDispatchZoneModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedZone(null);
                }}
                onSuccess={() => fetchZones(currentPage, perPage)}
                zone={selectedZone}
            />
        </AdminDashboardLayout>
    );
};

export default AdminZipcodeDispatchZonesPage;
